import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import ProductCard from "@/components/ProductCard";
import { BookOpen, PlayCircle } from "lucide-react";

export default function Author() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setErr(false);
    api
      .get(`/creators/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setErr(true));
  }, [slug]);

  if (err) {
    return (
      <Layout>
        <div className="container-narrow py-32 text-center" data-testid="author-not-found">
          <p className="font-serif text-2xl text-brand-ink">Author not found.</p>
          <Link to="/books" className="overline text-brand-rust mt-4 inline-block">Back to the library →</Link>
        </div>
      </Layout>
    );
  }
  if (!data) {
    return <Layout><div className="container-narrow py-32 overline text-brand-inkMuted">Loading…</div></Layout>;
  }

  return (
    <Layout>
      {/* HERO */}
      <section className="border-b border-brand-line">
        <div className="container-narrow py-20 md:py-28 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-4">
            {data.picture ? (
              <div className="aspect-[4/5] overflow-hidden border border-brand-line bg-brand-bg">
                <img src={data.picture} alt={data.name} className="w-full h-full object-cover" data-testid="author-picture" />
              </div>
            ) : (
              <div className="aspect-[4/5] bg-brand-bg border border-brand-line" />
            )}
          </div>
          <div className="lg:col-span-8">
            <span className="overline text-brand-rust">Author & Instructor</span>
            <h1 className="font-display text-5xl md:text-6xl mt-4 text-brand-ink leading-[1.05]" data-testid="author-name">
              {data.name}
            </h1>
            {data.bio && (
              <p className="mt-6 text-lg md:text-xl text-brand-inkMuted leading-relaxed max-w-2xl" data-testid="author-bio">
                {data.bio}
              </p>
            )}
            <div className="mt-10 flex flex-wrap gap-10 overline text-brand-inkMuted">
              <div className="flex items-center gap-2"><BookOpen className="w-4 h-4 text-brand-rust" /> {data.books.length} {data.books.length === 1 ? "book" : "books"}</div>
              <div className="flex items-center gap-2"><PlayCircle className="w-4 h-4 text-brand-rust" /> {data.courses.length} {data.courses.length === 1 ? "course" : "courses"}</div>
            </div>
          </div>
        </div>
      </section>

      {/* BOOKS */}
      {data.books.length > 0 && (
        <section className="container-narrow py-20" data-testid="author-books-section">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="overline text-brand-rust">By {data.name}</span>
              <h2 className="font-display text-3xl md:text-4xl mt-2 text-brand-ink">Books</h2>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {data.books.map((p, i) => <ProductCard key={p.product_id} product={p} index={i} />)}
          </div>
        </section>
      )}

      {/* COURSES */}
      {data.courses.length > 0 && (
        <section className={`container-narrow ${data.books.length > 0 ? "pb-20" : "py-20"}`} data-testid="author-courses-section">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="overline text-brand-rust">By {data.name}</span>
              <h2 className="font-display text-3xl md:text-4xl mt-2 text-brand-ink">Courses</h2>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.courses.map((p, i) => <ProductCard key={p.product_id} product={p} index={i} />)}
          </div>
        </section>
      )}
    </Layout>
  );
}
